/**
 * The Roles tree, as pure functions (D57). The tree itself comes from `GET /roles/levers`; the role's state is only its set of
 * held lever ids. A group's switch is derived from that set and never stored: the server checks levers, never groups.
 *
 * The founder's rule: turning a group on assigns every lever in it, and any single lever can then be turned off and stays off.
 * Nothing here re-applies a group after a lever is toggled.
 */
import { diffLevers, type LeverId } from "@helmr/contracts";
import type { GroupNode, LeverNode, LeverTree } from "../../api/roles";

export type GroupState = "on" | "off" | "partial";
/** Can the signed-in person grant or remove this lever? (They must hold it themselves; the owner holds all.) */
export type CanGrant = (id: LeverId) => boolean;

export const groupIds = (g: GroupNode): LeverId[] => g.levers.map((l) => l.id);

export function groupCount(g: GroupNode, held: ReadonlySet<LeverId>): { on: number; total: number } {
  return { on: g.levers.filter((l) => held.has(l.id)).length, total: g.levers.length };
}

export function groupState(g: GroupNode, held: ReadonlySet<LeverId>): GroupState {
  const { on, total } = groupCount(g, held);
  if (on === 0) return "off";
  return on === total ? "on" : "partial";
}

/** Levers in the group this person may not grant. The master switch skips them. */
export const lockedIn = (g: GroupNode, canGrant: CanGrant): LeverNode[] => g.levers.filter((l) => !canGrant(l.id));

/**
 * What a tap on the group switch does. Off or partial: every lever you can grant goes on. On: every lever you can grant goes off.
 * "On" is judged over the levers you can grant, so a group holding a lever you cannot touch can still be switched off by you.
 * Levers you cannot grant keep whatever state they have.
 */
export function toggleGroup(g: GroupNode, held: ReadonlySet<LeverId>, canGrant: CanGrant): Set<LeverId> {
  const mine = g.levers.filter((l) => canGrant(l.id));
  const next = new Set(held);
  const allOn = mine.length > 0 && mine.every((l) => held.has(l.id));
  for (const l of mine) allOn ? next.delete(l.id) : next.add(l.id);
  return next;
}

/** One lever on or off. It touches nothing else, so a lever turned off under an "on" group stays off. */
export function setLever(held: ReadonlySet<LeverId>, id: LeverId, on: boolean): Set<LeverId> {
  const next = new Set(held);
  on ? next.add(id) : next.delete(id);
  return next;
}

/**
 * A lever the registry added after this role was last saved. `roleVersion` is `role.leverRegistryVersion ?? 1` for a saved role
 * (a role the backfill has not reached counts as version 1) and the tree's own version for a role being created, which has nothing new.
 */
export const isNewLever = (l: LeverNode, roleVersion: number): boolean => l.addedIn > roleVersion;

export function newLeversIn(g: GroupNode, roleVersion: number): LeverNode[] {
  return g.levers.filter((l) => isNewLever(l, roleVersion));
}

/** "Turn on new levers": only the levers added since the role was saved, and only those you can grant. */
export function turnOnNew(g: GroupNode, held: ReadonlySet<LeverId>, roleVersion: number, canGrant: CanGrant): Set<LeverId> {
  const next = new Set(held);
  for (const l of newLeversIn(g, roleVersion)) if (canGrant(l.id)) next.add(l.id);
  return next;
}

/** The levers this one needs that are off. A hint only: nothing is toggled for you. */
export function unmetNeeds(l: LeverNode, held: ReadonlySet<LeverId>, tree: LeverTree): LeverNode[] {
  const byId = leverIndex(tree);
  return l.requires.filter((id) => !held.has(id)).map((id) => byId.get(id)).filter((x): x is LeverNode => x !== undefined);
}

export function leverIndex(tree: LeverTree): Map<string, LeverNode> {
  return new Map(tree.groups.flatMap((g) => g.levers.map((l) => [l.id, l] as const)));
}

export function groupOf(tree: LeverTree, id: string): GroupNode | undefined {
  return tree.groups.find((g) => g.levers.some((l) => l.id === id));
}

/** Levers whose label contains the query (case and accents ignored). An empty query matches everything. Groups with no match drop out. */
export function filterTree(tree: LeverTree, query: string): GroupNode[] {
  const q = fold(query);
  if (!q) return tree.groups;
  return tree.groups.map((g) => ({ ...g, levers: g.levers.filter((l) => fold(l.label).includes(q)) })).filter((g) => g.levers.length > 0);
}

const fold = (s: string) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();

/** Groups that start open: the ones that are partial (the ones a person came to adjust). */
export const startsExpanded = (g: GroupNode, held: ReadonlySet<LeverId>): boolean => groupState(g, held) === "partial";

/** Added and removed lever ids, sorted. */
export function changeOf(before: Iterable<LeverId>, after: Iterable<LeverId>) {
  return diffLevers(before, after);
}

/** "Adding 3 · removing 1", or "No lever changes". */
export function diffText(d: { added: readonly unknown[]; removed: readonly unknown[] }): string {
  const parts: string[] = [];
  if (d.added.length) parts.push(`Adding ${d.added.length}`);
  if (d.removed.length) parts.push(`removing ${d.removed.length}`);
  if (parts.length === 0) return "No lever changes";
  // "Adding 3 · removing 1" / "Removing 1" when nothing is added.
  if (!d.added.length) parts[0] = "Removing " + d.removed.length;
  return parts.join(" · ");
}

/** Labels for a list of lever ids, in tree order. Unknown ids (a lever from a newer server) show as their id. */
export function labelsOf(tree: LeverTree, ids: readonly LeverId[]): { id: LeverId; label: string; group: string }[] {
  const idx = leverIndex(tree);
  const order = new Map([...idx.keys()].map((k, i) => [k, i]));
  return [...ids]
    .sort((a, b) => (order.get(a) ?? 1e9) - (order.get(b) ?? 1e9))
    .map((id) => ({ id, label: idx.get(id)?.label ?? id, group: groupOf(tree, id)?.label ?? "" }));
}
